import Clickable from 'components/Common/Clickable'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import LineCarIcon from 'components/Luxkit/Icons/line/LineCarIcon'
import LineChairWithAirplaneIcon from 'components/Luxkit/Icons/line/LineChairWithAirplaneIcon'
import LineCompassIcon from 'components/Luxkit/Icons/line/LineCompassIcon'
import LineHotAirBalloonIcon from 'components/Luxkit/Icons/line/LineHotAirBalloonIcon'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import LineMinusIcon from 'components/Luxkit/Icons/line/LineMinusIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import LineRedoIcon from 'components/Luxkit/Icons/line/LineRedoIcon'
import LineRoomUpgradeIcon from 'components/Luxkit/Icons/line/LineRoomUpgradeIcon'
import LineShipIcon from 'components/Luxkit/Icons/line/LineShipIcon'
import LineTaxiIcon from 'components/Luxkit/Icons/line/LineTaxiIcon'
import SolidBedIcon from 'components/Luxkit/Icons/solid/SolidBedIcon'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import { setSvgIconSize } from 'components/Luxkit/SvgIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Typography from 'components/Luxkit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import useOrder from 'hooks/Order/useOrder'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { diffInDays } from 'lib/datetime/dateUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const ActivityRow = styled.div`
  display: grid;
  width: 100%;
  padding: 0 ${rem(8)};
  grid-template-columns: 1fr min-content ${rem(72)} ${rem(32)};
  min-height: ${rem(56)};
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s, min-height 0.2s;

  &.expanded {
    min-height: ${rem(128)};
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr ${rem(72)} ${rem(102)} ${rem(72)};
    padding: 0 ${rem(20)} 0 0;
  }
`

const ActivityIcon = styled.div`
  height: ${rem(24)};
  width: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: grid;
  place-items: center;
  ${setSvgIconSize(12)};
  flex-shrink: 0;
`

interface ActivityDetails {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  duration?: string;
}

interface Props {
  activity: App.LoyaltyAccountActivity;
}

function LoyaltyAccountLandingPageUserActivity(props: Props) {
  const { activity } = props
  const [order, fetchingOrder] = useOrder(activity.orderId)
  const [expanded, setExpanded] = useState<boolean>(false)

  const { icon, title, subtitle, duration } = useMemo((): ActivityDetails => {
    switch (activity.type) {
      case 'enrolment':
        return { title: 'Loyalty program enrolment' }
      case 'expired_points':
        return { title: 'Points expired' }
      case 'manual':
        return { title: 'Manual adjustment' }
      case 'tier_change':
        return { title: 'Tier change', subtitle: activity.description }
      case 'expired_points_reversal':
        return { title: 'Points expired reversal' }
      case 'other':
        return { title: activity.description ?? 'Other' }
      case 'cancellation':
      case 'benefit_redemption':
      case 'earn_points':
      case 'spend_points':
      case 'earn_status_credits':
      case 'pending_points': {
        const benefitCode = order?.loyaltyDetails.benefitsRedeemed.find(benefit => benefit.itemId === activity.orderItemId)?.code
        const hotelItem = order?.items.find(item => item.id === activity.orderItemId)
        if (hotelItem) {
          return {
            icon: benefitCode === 'hotel_upgrade' ? <LineRoomUpgradeIcon /> : <SolidBedIcon />,
            title: benefitCode === 'hotel_upgrade' ? 'Room upgrade' : hotelItem.offer.property?.name!,
            subtitle: hotelItem.reservation?.roomType?.name,
            duration: hotelItem.duration ? pluralizeToString('night', hotelItem.duration) : '-',
          }
        }

        const bedbankItem = order?.bedbankItems.find(item => item.id === activity.orderItemId)
        if (bedbankItem) {
          return {

            icon: <SolidBedIcon />,
            title: bedbankItem.offer.property.name,
            subtitle: bedbankItem.offer.packages[0].rate.room.name,
            duration: pluralizeToString('night', bedbankItem.duration),
          }
        }

        const experienceItem = order?.experienceItems.find(item => item.id === activity.orderItemId)
        if (experienceItem) {
          let icon = <LineHotAirBalloonIcon />
          if (benefitCode === 'airport_transfer') {
            icon = <LineTaxiIcon />
          } if (benefitCode === 'lounge_pass') {
            icon = <LineChairWithAirplaneIcon />
          }
          return {
            icon,
            title: activity.type === 'benefit_redemption' ? activity.description! : experienceItem.ticket.name,
            subtitle: activity.type === 'benefit_redemption' ? experienceItem.ticket.name : '',
          }
        }

        const cruiseItem = order?.cruiseItems.find(item => item.id === activity.orderItemId)
        if (cruiseItem) {
          const duration = diffInDays(new Date(cruiseItem.departureDate), new Date(cruiseItem.arrivalDate))
          return {
            icon: <LineShipIcon />,
            title: cruiseItem.offerName!,
            duration: pluralizeToString('day', duration),
          }
        }

        const insuranceItem = order?.insuranceItems.find(item => item.id === activity.orderItemId) || order?.bookingProtectionItems.find(item => item.id === activity.orderItemId)
        if (insuranceItem) {
          return { title: insuranceItem.productName }
        }

        const tourItem = order?.tourItems.find(item => item.id === activity.orderItemId)
        if (tourItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourItem.tour.title,
            duration: pluralizeToString('day', tourItem.tour.duration),
          }
        }

        const tourExperienceItem = order?.tourExperienceItems.find(item => item.id === activity.orderItemId)
        if (tourExperienceItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourExperienceItem.name,
          }
        }

        const flightItem = order?.flightItems.find(item => item.id === activity.orderItemId)
        if (flightItem) {
          return {

            icon: <LinePlaneDepartureIcon />,
            title: 'Flight',
            subtitle: flightItem.travellers?.map(traveller => `${traveller.givenName} ${traveller.surname}`).join(', '),
          }
        }

        const carHireItem = order?.carHireItems.find(item => item.id === activity.orderItemId)
        if (carHireItem) {
          const duration = carHireItem.reservation ? diffInDays(new Date(carHireItem.reservation.pickUp.date), new Date(carHireItem.reservation.dropOff.date)) : undefined
          return {
            icon: <LineCarIcon />,
            title: carHireItem.reservation?.vehicle.model ?? 'Car hire',
            duration: duration ? pluralizeToString('day', duration) : '-',
          }
        }

        const subscriptionItem = order?.subscriptionItems.find(item => item.id === activity.orderItemId)
        if (subscriptionItem) {
          return { title: 'LuxPlus+ membership' }
        }

        // fallbacks if we didn't find any order items to describe the activity types
        switch (activity.type) {
          case 'pending_points':
            return { title: activity.description!, subtitle: 'Pending points' }
          case 'benefit_redemption':
            return { title: activity.description!, subtitle: 'Benefit redemption' }
          case 'earn_points':
            return { title: activity.description!, subtitle: 'Points earned' }
          case 'spend_points':
            return { title: activity.description!, subtitle: 'Points spent' }
          case 'earn_status_credits':
            return { title: activity.description!, subtitle: 'Status credits earned' }
          case 'cancellation':
            return { title: activity.description!, subtitle: 'Cancellation' }
        }
      }
    }
  }, [activity, order])

  const isPending = activity.type === 'pending_points'
  const pointsIcon = useMemo(() => {
    if (isPending) {
      return <LineRedoIcon colour="warning" size={12} />
    } else if (activity.basePoints > 0) {
      return <LinePlusIcon colour="success" size={12} />
    } else if (activity.basePoints < 0) {
      return <LineMinusIcon colour="critical" size={12} />
    }
  }, [activity, isPending])

  const isMobile = useScreenSizeOnly('mobile')

  const onRowClick = useCallback(() => {
    if (isMobile) {
      setExpanded(expanded => !expanded)
    }
  }, [isMobile])

  if (fetchingOrder) {
    return <ActivityRow><LoadingBox floating /></ActivityRow>
  }

  return <ActivityRow
    as={isMobile ? Clickable : undefined}
    onClick={isMobile ? onRowClick : undefined }
    className={isMobile && expanded ? 'expanded' : undefined}
  >
    <Group
      style={{ overflow: 'hidden' }}
      direction="horizontal"
      horizontalAlign="space-between"
      verticalAlign="center"
      gap={8}
    >
      <Group
        direction="horizontal"
        gap={8}
        verticalAlign="center"
        style={{ overflow: 'hidden' }}
      >
        <ActivityIcon>{icon || <LineInfoCircleIcon />}</ActivityIcon>
        <Group direction="vertical" gap={2} style={{ overflow: 'hidden' }}>
          <BodyText variant="medium" colour="neutral-two" wrap={expanded ? 'word-wrap' : 'truncate'}>{title}</BodyText>
          {!!subtitle && <CSSBreakpoint min="tablet">
            <BodyText variant="small" colour="neutral-two" wrap={expanded ? 'word-wrap' : 'truncate'}>
              {subtitle}
            </BodyText>
          </CSSBreakpoint>}
          <CSSBreakpoint max="mobile">
            {expanded && <>
              {subtitle && <BodyText variant="small" colour="neutral-three" lineClamp={3} title={subtitle}>
                {subtitle}
              </BodyText>}
              <BodyText variant="medium" colour="neutral-three">
                <FormatDateTime value={activity.date} format="dmy-short" />
              </BodyText>
              {isPending && <StatusLabel variant="pending">Pending</StatusLabel>}
              {activity.type === 'benefit_redemption' && <StatusLabel variant="confirmation">Redeemed</StatusLabel>}
            </>}
            {!!duration && <BodyText variant="small" colour="neutral-two" wrap={expanded ? 'word-wrap' : 'truncate'}>
              {duration}
            </BodyText>}
          </CSSBreakpoint>
        </Group>
      </Group>
      <CSSBreakpoint min="tablet">
        <BodyText variant="medium" colour="neutral-two">
          <FormatDateTime value={activity.date} format="dmy-short" />
        </BodyText>
      </CSSBreakpoint>
    </Group>
    <CSSBreakpoint min="tablet">
      <BodyText variant="medium" colour="neutral-two" align="end">
        {duration ?? '-'}
      </BodyText>
    </CSSBreakpoint>
    <Group direction="vertical" horizontalAlign="end" gap={4}>
      <BodyTextBlock
        variant="medium"
        startIcon={pointsIcon}
        colour={isPending ? 'neutral-three' : 'neutral-two'}
        align="end"
      >
        {!!activity.basePoints && <FormatDecimal value={Math.abs(activity.basePoints)} />}
        {!activity.basePoints && '-'}
      </BodyTextBlock>
    </Group>
    <BodyText variant="medium" colour="neutral-two" align="end">
      {activity.statusCredits ? <FormatDecimal value={activity.statusCredits} /> : '-'}
    </BodyText>
    <CSSBreakpoint max="mobile">
      <Typography align="end">
        <DropdownChevron open={expanded} size="S" />
      </Typography>
    </CSSBreakpoint>
  </ActivityRow>
}

export default LoyaltyAccountLandingPageUserActivity
